import type { SearchFilter } from "@depict-ai/types/api/SearchResponse";
import { isDashedSize, isNumeric } from "../utils";
// Removed unused import for FilterGroupStoryblok

export type FormattedFilterGroup = {
	id: string;
	label: string;
	key: string;
	title: string;
	values: FormattedFilter[];
};

export type CustomFormattedFilter = {
	name: string;
	value: string;
	count: number;
	hex?: string;
	field: string;
};

export type CustomFormattedFilterGroup = Omit<FormattedFilterGroup, "values"> & {
	values: CustomFormattedFilter[];
	children?: CustomFormattedFilterGroup[];
	checkboxOnly?: boolean;
};

export interface DepictMeta2 {
	group_title: string;
	type: string;
	values: string[];
	counts: number[];
	names: string[];
	swatches?: string[];
}

export interface FormattedFilter {
	name: string;
	value: string;
	count: number;
	hex?: string;
}

export type FilterValue = string[] | string;

export interface FilterMeta {
	group_title: string;
	type: string;
	values: FilterValue[];
	counts: number[];
	names: string[];
}

export interface DepictFilter {
	field: string;
	op: string;
	meta: DepictMeta2;
	id: string;
}

// Depict's `var_color.value` facet only exposes the localized display name, never a
// locale-independent code, and its swatch hex for "multi color" is an unreliable,
// per-locale-index artifact (e.g. "#fffeff" for en_GB/no_NO, null elsewhere) rather
// than a stable sentinel — so detection has to match on known translations instead.
const MULTI_COLOR_NAMES = new Set([
	"multi color",
	"multi colour",
	"flerfarget", // Norwegian
	"flerfarvet", // Danish
	"mehrfarbig", // German
	"flerfärgad", // Swedish
]);

export const isMultiColorName = (name?: string | null) => {
	if (!name) return false;
	const normalized = name.trim().toLowerCase();
	return (
		MULTI_COLOR_NAMES.has(normalized) || normalized.includes("multi") || normalized.includes("print")
	);
};

const overrideLabel = (label: string) => {
	if (label === "product_scollection_category_hierarchy") {
		return "category";
	}
	return label;
};

function parseFeatureValues(valueList: string[]): string[] {
	const uniqueFeatures = new Set<string>();

	valueList.forEach((valueStr) => {
		try {
			// Replace single quotes with double quotes to convert into valid JSON array
			const features: string[] = JSON.parse(valueStr.replace(/'/g, '"'));
			features.forEach((feature) => uniqueFeatures.add(feature));
		} catch (err) {
			console.warn("Failed to parse feature string:", valueStr);
		}
	});

	return Array.from(uniqueFeatures).sort();
}

const sortSizes = (arr: FormattedFilter[]) => {
	arr.sort((a, b) => {
		if (isNumeric(a.value) && isNumeric(b.value)) {
			return Number(a.value) - Number(b.value);
		}
		if (isDashedSize(a.value) && !isDashedSize(b.value)) return 1;
		if (!isDashedSize(a.value) && isDashedSize(b.value)) return -1;
		if (a.value === "ONESZ") return 1;
		if (b.value === "ONESZ") return -1;
		return 0;
	});
	return arr;
};

export const createFormattedFilters = (filters: DepictFilter[] | null) => {
	const filterGroups: FormattedFilterGroup[] = [];
	if (!filters) return filterGroups;

	for (const group of filters) {
		if (group?.meta?.type === "checkbox") {
			const checkboxGroup: FormattedFilterGroup = {
				title: group.meta.group_title ?? "",
				key: group.field ?? "",
				label: group.field ?? "",
				id: group.id ?? "",
				values: group.meta.values.map((value, i) => ({
					value,
					name: group?.meta?.names?.[i] ?? "",
					count: group?.meta?.counts?.[i] ?? 0,
				})).filter((item) => item.count > 0),
			};

			filterGroups.push(checkboxGroup);
			continue;
		}

		// Handle regular filters
		const formattedGroup: FormattedFilterGroup = {
			title: group.meta.group_title,
			key: group.field,
			label: group.field,
			id: group.id,
			values: group.meta.values?.map((value, i) => {
				/* 		let hex;
				if (group.meta.group_title === "Colour" && typeof value === "string") {
					hex = value.split(";")[1];
				} */

				return {
					value: value,
					name: group.meta.names[i],
					count: group.meta.counts[i],
					hex: group.meta.swatches?.[i],
				};
			}).filter((item) => item.count > 0) as FormattedFilter[],
		};

		if (group.field === "size_name" || group.meta.group_title.toLowerCase().includes("size")) {
			formattedGroup.values = sortSizes(formattedGroup.values);
		}

		filterGroups.push(formattedGroup);
	}

	return filterGroups;
};
